import { useMutation, useQueryClient } from "@tanstack/react-query";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import { useCompany } from "#modules/company/view/useCompany";
import { isOptimizationInvalidationCancelled } from "#modules/optimizations/infra/optimizationInvalidationConfirmation";
import {
  applyOptimisticUpdatePartType,
  applyUpdatePartTypeSuccess,
  rollbackUpdatePartType,
  UpdatePartTypeCacheSnapshot,
} from "#modules/parts/infra/updatePartTypeCache";
import { AnalyticsEvent, useAnalytics } from "#shared/useAnalytics";
import { JobStatusSchema, QuoteRequestLine } from "shared";

import { bulkUpdatePartType, BulkUpdatePartTypeDto } from "../infra/bulkUpdatePartType.api";

type MutationData = Omit<BulkUpdatePartTypeDto, "ids"> & {
  quoteRequestLines: QuoteRequestLine[];
};

type Context = {
  snapshots: UpdatePartTypeCacheSnapshot[];
};

export const useBulkUpdatePartType = () => {
  const queryClient = useQueryClient();
  const analytics = useAnalytics();

  const { company } = useCompany();

  const { mutate, ...rest } = useMutation({
    mutationFn: ({ partType, partSubType, quoteRequestLines }: MutationData) =>
      bulkUpdatePartType({ partType, partSubType, ids: quoteRequestLines.map((qrl) => qrl.id) }),

    onMutate: async ({ partType, quoteRequestLines }: MutationData) => {
      const snapshots = await Promise.all(
        quoteRequestLines.map((quoteRequestLine) =>
          applyOptimisticUpdatePartType(queryClient, {
            partId: quoteRequestLine.partId,
            quoteRequestLineId: quoteRequestLine.id,
            newPartType: partType,
          }),
        ),
      );

      quoteRequestLines.forEach((quoteRequestLine) => {
        if (quoteRequestLine.predictionStatus === JobStatusSchema.enum.PROCESSED) {
          analytics.capture(AnalyticsEvent.PredictionRerun, {
            quote_request_line_id: quoteRequestLine.id,
            trigger: "bulkUpdatePartType",
            company_id: company?.id ?? null,
            company_name: company?.name ?? null,
          });
        }
      });

      return { snapshots };
    },

    onSuccess: (result) => {
      result.results.forEach(
        ({ quoteRequestLineId, partId, part, shouldAnalyzePart, shouldPredictAfterAnalyze }) => {
          applyUpdatePartTypeSuccess(queryClient, {
            partId,
            quoteRequestLineId,
            part,
            shouldAnalyzePart,
            shouldPredictAfterAnalyze,
          });
        },
      );
    },

    onError: (err: Error, _: MutationData, context: Context | undefined) => {
      if (!isOptimizationInvalidationCancelled(err)) {
        showErrorToast({ title: err.message });
        errorHandler(err);
      }

      context?.snapshots.forEach((snapshot) => {
        rollbackUpdatePartType(queryClient, snapshot);
      });
    },
  });

  return { bulkUpdatePartType: mutate, ...rest };
};
